
import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { scheduleData } from '../../mocks/scheduleData';
import ScheduleHeader from './components/ScheduleHeader';
import ScheduleFooter from './components/ScheduleFooter';
import ScheduleFilters from './components/ScheduleFilters';
import ScheduleCard from './components/ScheduleCard';
import ScheduleCalendar from './components/ScheduleCalendar';
import ScheduleDetailModal from './components/ScheduleDetailModal';

type ViewMode = 'list' | 'calendar';

interface ScheduleItem {
  id: number;
  date: string;
  day: string;
  time: string;
  doorOpen: string;
  venue: string;
  artist: string;
  title: string;
  genre: string;
  price: number;
  ticketUrl: string;
  status: string;
  description: string;
}

export default function SchedulePage() {
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedMonth, setSelectedMonth] = useState('2025-07');
  const [selectedGenre, setSelectedGenre] = useState('すべて');
  const [selectedVenue, setSelectedVenue] = useState('すべて');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItem, setSelectedItem] = useState<ScheduleItem | null>(null);

  const filteredSchedules = useMemo(() => {
    return scheduleData.filter((item) => {
      const matchMonth = item.date.startsWith(selectedMonth);
      const matchGenre = selectedGenre === 'すべて' || item.genre === selectedGenre;
      const matchVenue = selectedVenue === 'すべて' || item.venue === selectedVenue;
      const matchSearch =
        !searchQuery ||
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.artist.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.venue.toLowerCase().includes(searchQuery.toLowerCase());
      return matchMonth && matchGenre && matchVenue && matchSearch;
    });
  }, [selectedMonth, selectedGenre, selectedVenue, searchQuery]);

  return (
    <div className="min-h-screen bg-white">
      <ScheduleHeader />

      <main className="pt-20">
        {/* Hero */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/91089ed5a4fb78b6023ed908385f2e94.jpg"
              alt="公演スケジュール"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/70"></div>
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-6">
            <div className="flex items-center gap-2 text-sm text-white/60 mb-6">
              <Link to="/" className="hover:text-white transition-colors cursor-pointer">トップページ</Link>
              <i className="ri-arrow-right-s-line text-white/40"></i>
              <span className="text-white font-medium">公演スケジュール</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              <a href="#schedule-content" className="hover:opacity-90 transition-opacity">公演スケジュール</a>
            </h1>
            <p className="text-lg text-white/80 max-w-2xl leading-relaxed">
              東京支部参加会場で開催されるライブ公演の最新スケジュールです。チケットは公演の約1ヶ月前よりEプラス等にて販売開始予定です。
            </p>
          </div>
        </section>

        {/* Main Content */}
        <section id="schedule-content" className="py-12">
          <div className="max-w-7xl mx-auto px-6">
            {/* View Toggle */}
            <div className="flex items-center justify-between mb-8">
              <div className="inline-flex bg-gray-100 rounded-full p-1">
                <button
                  onClick={() => setViewMode('list')}
                  className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    viewMode === 'list'
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <i className="ri-list-unordered"></i>
                  リスト表示
                </button>
                <button
                  onClick={() => setViewMode('calendar')}
                  className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    viewMode === 'calendar'
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <i className="ri-calendar-line"></i>
                  カレンダー表示
                </button>
              </div>
            </div>

            {/* Filters */}
            <ScheduleFilters
              selectedMonth={selectedMonth}
              onMonthChange={setSelectedMonth}
              selectedGenre={selectedGenre}
              onGenreChange={setSelectedGenre}
              selectedVenue={selectedVenue}
              onVenueChange={setSelectedVenue}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              resultCount={filteredSchedules.length}
            />

            {/* Content */}
            <div className="mt-8">
              {viewMode === 'list' ? (
                filteredSchedules.length > 0 ? (
                  <div className="space-y-4">
                    {filteredSchedules.map((item) => (
                      <ScheduleCard
                        key={item.id}
                        item={item}
                        onDetailClick={setSelectedItem}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-20">
                    <i className="ri-calendar-close-line text-4xl text-gray-300 mb-4 block"></i>
                    <p className="text-gray-500 text-sm">該当する公演が見つかりませんでした。</p>
                    <p className="text-gray-400 text-xs mt-1">フィルター条件を変更してお試しください。</p>
                  </div>
                )
              ) : (
                <ScheduleCalendar
                  schedules={filteredSchedules}
                  selectedMonth={selectedMonth}
                  onEventClick={setSelectedItem}
                />
              )}
            </div>
          </div>
        </section>

        {/* Info Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">ご来場にあたって</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg p-6 border border-gray-200">
                <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-lg mb-4">
                  <i className="ri-ticket-2-line text-gray-700 text-lg"></i>
                </div>
                <h3 className="text-sm font-bold text-gray-900 mb-2">チケット購入</h3>
                <p className="text-xs text-gray-500 leading-relaxed">
                  チケットは公演の約1ヶ月前よりEプラス等にて販売開始予定です。各会場の公式サイトでも最新情報をご確認いただけます。
                </p>
              </div>
              <div className="bg-white rounded-lg p-6 border border-gray-200">
                <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-lg mb-4">
                  <i className="ri-goblet-line text-gray-700 text-lg"></i>
                </div>
                <h3 className="text-sm font-bold text-gray-900 mb-2">ドリンク代</h3>
                <p className="text-xs text-gray-500 leading-relaxed">
                  多くの会場では入場時に別途ドリンク代（500〜600円程度）が必要です。詳細は各会場の案内をご確認ください。
                </p>
              </div>
              <div className="bg-white rounded-lg p-6 border border-gray-200">
                <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-lg mb-4">
                  <i className="ri-megaphone-line text-gray-700 text-lg"></i>
                </div>
                <h3 className="text-sm font-bold text-gray-900 mb-2">SNS告知</h3>
                <p className="text-xs text-gray-500 leading-relaxed">
                  各公演の詳細情報は公演の約2週間前よりSNS等で告知されます。最新情報は各アーティスト・会場のSNSをフォローしてください。
                </p>
              </div>
            </div>

            <div className="mt-8 bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-start gap-3">
                <i className="ri-information-line text-gray-400 mt-0.5"></i>
                <div>
                  <p className="text-sm text-gray-700 font-medium mb-2">スケジュールに関するご注意</p>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    掲載内容は変更になる場合があります。最新の情報は各会場の公式サイトにてご確認ください。出演者の都合により、出演順や出演者が変更になる場合がございます。あらかじめご了承ください。スケジュールの掲載・修正依頼は支部窓口までお問い合わせください。
                  </p>
                </div>
              </div>
            </div>

            <div className="text-center mt-8">
              <a
                href="/#contact"
                className="inline-flex items-center gap-2 bg-gray-900 text-white px-8 py-3 rounded-md text-sm font-semibold hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-mail-send-line"></i>
                支部窓口へお問い合わせ
              </a>
            </div>
          </div>
        </section>
      </main>

      <ScheduleFooter />

      {/* Detail Modal */}
      <ScheduleDetailModal item={selectedItem} onClose={() => setSelectedItem(null)} />
    </div>
  );
}
